"use client";
// App-wide analytics provider. Installs the B2C journey beacon for the logged-in
// candidate and exposes a `useAnalytics().track(name, props)` hook. Mounted once
// under AppStateProvider (in ProtectedRoute) so it sees `userProfile`. The
// beacon is uid-gated, so on unauthenticated pages (marketing, the interview
// room) it stays dormant. Instrumentation is opt-in per component — there is no
// route-change auto-tracking.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppStateContext } from "@/components/context/AppContext";
import { setupJourneyAnalytics, emitJourneyEvent } from "@/lib/analytics/beacon";
import { initMixpanel, mpIdentify, mpPeopleSet } from "@/lib/analytics/mixpanel";
import {
  getAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";

type TrackFn = (name: string, props?: Record<string, unknown>) => void;

const AnalyticsContext = createContext<{ track: TrackFn }>({ track: () => {} });

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const ctx = useContext(AppStateContext) as
    | {
        userProfile?: {
          uid?: string;
          first_name?: string;
          last_name?: string;
          email?: string;
          role?: string;
        };
      }
    | undefined;
  const profile = ctx?.userProfile;
  const uid = profile?.uid;
  const [consent, setConsent] = useState(getAnalyticsConsent);

  useEffect(() => subscribeToAnalyticsConsent(setConsent), []);

  // Mixpanel runs alongside the journey beacon: init once, then identify on the
  // same uid signal so both streams attribute to the same Firebase user.
  useEffect(() => {
    if (consent === "accepted") initMixpanel();
  }, [consent]);

  useEffect(() => {
    if (consent !== "accepted") return;
    setupJourneyAnalytics({ uid });
    mpIdentify(uid);
    // Enrich the Mixpanel User Profile so people show as name/email, not a raw
    // uid. Set after identify so it attaches to the canonical user.
    if (!uid) return;
    const name = [profile?.first_name, profile?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    const props: Record<string, unknown> = {};
    if (name) props.$name = name;
    if (profile?.email) props.$email = profile.email;
    if (profile?.role) props.role = profile.role;
    if (Object.keys(props).length > 0) mpPeopleSet(props);
  }, [consent, uid, profile?.first_name, profile?.last_name, profile?.email, profile?.role]);

  const track = useCallback<TrackFn>(
    (name, props) => emitJourneyEvent(name, props),
    []
  );

  return (
    <AnalyticsContext.Provider value={{ track }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}
